import { ISearchBoxStyles } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../src/taskpane/components/_v2/redux/storeMain";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { SearchBox } from "../../../src/taskpane/components/directoryComponents/SearchBox/SearchBox";

const searchBoxStyles: Partial<ISearchBoxStyles> = {
  root: { width: 300, height: "100%" }
};

const QUICK_FILTER_DEBOUNCE_MS = 500;

/**
 * Presentational only — the typed text is owned by useMenuItem_QuickFilter one level up.
 * The command bar drops this item while rows are selected (see the gate in
 * ContentBodyFilesFolders), so this component unmounts on every selection: state kept in
 * here would die with it and take the user's filter along. Same reason the aggregate view
 * controls its box from parent state and advanced search keeps its value in the hook.
 */
const QuickFilterBox = React.memo(({
  value,
  onChange,
  onSearch,
  onClear
}: {
  value: string,
  onChange: (event: any, newValue?: string) => void,
  onSearch: (filterText: any) => void,
  onClear: () => void
}) => {
  const languageStrings = React.useContext(LanguageContext);

  return (
    <SearchBox
      styles={searchBoxStyles}
      placeholder={languageStrings.FilterResults}
      underlined={true}
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      onClear={onClear}
      showIcon={true}
    />
  );
});

export const useMenuItem_QuickFilter = (setQuickFilterValue: (filterValue: string | null) => void) => {
  const enableQuickFilter = useSelector((state: RootState) => state.tenantInfo?.enableQuickFilter);
  const searchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue?.value);
  const currentDirectory = useSelector((state: RootState) =>
    state.workingDirectories.workingDirectories.length > 0
      ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1]
      : null
  );

  // The text being typed, held above the box so it outlives the box. The APPLIED value
  // stays with the caller (it drives the grid); this is only the draft behind it.
  const [draftValue, setDraftValue] = React.useState<string>("");

  const clearFilter = React.useCallback(() => {
    setDraftValue("");
    setQuickFilterValue(null);
  }, [setQuickFilterValue]);

  // A quick filter describes the listing in front of the user, so it must not follow them
  // into another directory or outlive the search that produced the listing. Deliberately
  // keyed on navigation alone — this used to be the box's mount effect, which meant it
  // also fired every time a selection put the box back, discarding a live filter.
  //
  // Both effects stay inert for tenants without the feature: this hook now outlives the
  // box, so without the gate it would keep writing to the caller's filter state (and
  // re-render the grid on every navigation) for a control that is never rendered.
  React.useEffect(() => {
    if (!enableQuickFilter) {
      return;
    }
    setDraftValue("");
    setQuickFilterValue(null);
  }, [enableQuickFilter, currentDirectory, searchValue]);

  React.useEffect(() => {
    if (!enableQuickFilter) {
      return undefined;
    }
    const handler = setTimeout(() => {
      setQuickFilterValue(draftValue?.trim() || null);
    }, QUICK_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [enableQuickFilter, draftValue, setQuickFilterValue]);

  const onChange = React.useCallback((_, newValue?: string) => setDraftValue(newValue || ""), []);
  const onSearch = React.useCallback((filterText: any) => setQuickFilterValue(filterText?.trim() || null), [setQuickFilterValue]);

  const onMenuItem_QuickFilterRender = React.useCallback(() => {
    return <QuickFilterBox
      value={draftValue}
      onChange={onChange}
      onSearch={onSearch}
      onClear={clearFilter}
    />;
  }, [draftValue, onChange, onSearch, clearFilter]);

  const menuItem_QuickFilter = React.useMemo(() => {
    if (!enableQuickFilter) {
      return null;
    }

    return {
      key: "QuickFilter",
      onRender: onMenuItem_QuickFilterRender
    }
  }, [enableQuickFilter, onMenuItem_QuickFilterRender]);

  return {
    menuItem_QuickFilter,
    resetQuickFilterValue: clearFilter
  };
};
